'use client';

import { useEffect, useEffectEvent, useRef, useState } from 'react';

import { useAtomValue } from 'jotai';

import { useProjectResponseModeContext } from '@/shared-survey';
import { currentPlayingIdAtom } from '@/shared-ui';
import {
  getItemFromLocalStorage,
  setItemInLocalStorage,
} from '@/shared-utilities';

import { createHandsFreeAutoListenController } from './project-usability-handsfree-auto-listen.helpers';
import {
  HANDSFREE_ONBOARDING_FIRST_MESSAGE_DELAY_MS,
  HANDSFREE_ONBOARDING_STORAGE_KEY,
  HANDSFREE_ONBOARDING_STEPS,
} from './project-usability-handsfree-onboarding.consts';

interface UseHandsFreeOnboardingOptions {
  isRecording: boolean;
  enabled?: boolean;
}

interface UseHandsFreeOnboardingReturn {
  isOnboardingActive: boolean;
  currentOnboardingStep: number;
  isLastStep: boolean;
  handleOnboardingEnd: () => void;
  handleOnboardingNext: () => void;
}

export function useHandsFreeOnboarding({
  isRecording,
  enabled = true,
}: UseHandsFreeOnboardingOptions): UseHandsFreeOnboardingReturn {
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [currentOnboardingStep, setCurrentOnboardingStep] = useState(0);
  const hasTriggeredOnboardingRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;
    if (getItemFromLocalStorage(HANDSFREE_ONBOARDING_STORAGE_KEY) === true)
      return;
    if (hasTriggeredOnboardingRef.current) return;
    if (!isRecording) return;

    hasTriggeredOnboardingRef.current = true;
    const t = setTimeout(() => {
      setIsOnboardingActive(true);
      setCurrentOnboardingStep(0);
    }, HANDSFREE_ONBOARDING_FIRST_MESSAGE_DELAY_MS);
    return () => clearTimeout(t);
  }, [isRecording, enabled]);

  const handleOnboardingEnd = () => {
    if (typeof window !== 'undefined') {
      setItemInLocalStorage(HANDSFREE_ONBOARDING_STORAGE_KEY, true);
    }
    setIsOnboardingActive(false);
  };

  const handleOnboardingNext = () => {
    if (currentOnboardingStep >= HANDSFREE_ONBOARDING_STEPS.length - 1) {
      handleOnboardingEnd();
      return;
    }
    setCurrentOnboardingStep((prev) => prev + 1);
  };

  const lastOnboardingStepIndex = HANDSFREE_ONBOARDING_STEPS.length - 1;
  const isLastStep = currentOnboardingStep === lastOnboardingStepIndex;

  return {
    isOnboardingActive,
    currentOnboardingStep,
    isLastStep,
    handleOnboardingEnd,
    handleOnboardingNext,
  };
}

interface UseHandsFreeAutoListenOptions {
  enabled: boolean;
  isAiVoiceEnabled: boolean;
  isPaused?: boolean;
}

export function useHandsFreeAutoListen({
  enabled,
  isAiVoiceEnabled,
  isPaused = false,
}: UseHandsFreeAutoListenOptions) {
  const { startListening, isListening, responseMode } =
    useProjectResponseModeContext();
  const currentPlayingId = useAtomValue(currentPlayingIdAtom);
  const controllerRef = useRef<ReturnType<
    typeof createHandsFreeAutoListenController
  > | null>(null);

  const isEligible =
    enabled && isAiVoiceEnabled && responseMode === 'voice' && !isPaused;

  const startListeningEvent = useEffectEvent(startListening);
  const isListeningEvent = useEffectEvent(() => isListening);

  useEffect(() => {
    controllerRef.current ??= createHandsFreeAutoListenController({
      startListening: () => startListeningEvent(),
      isListening: () => isListeningEvent(),
    });

    controllerRef.current.onCurrentPlayingIdChange(
      currentPlayingId,
      isEligible
    );
    controllerRef.current.onEligibilityChange(isEligible);

    return () => {
      controllerRef.current?.stop();
    };
  }, [currentPlayingId, isEligible]);
}
